(function () {
  'use strict';

  // Parse quiz answers from URL
  function getQuizAnswers() {
    const params = new URLSearchParams(window.location.search);
    return {
      budget: parseInt(params.get('q1')) || null,
      height: parseInt(params.get('q2')) || null,
      hours: parseInt(params.get('q3')) || null,
      adjust: parseInt(params.get('q4')) || null,
      lumbar: parseInt(params.get('q5')) || null
    };
  }

  const QUESTION_WEIGHTS = {
    budget: 30,
    height: 10,
    hours: 25,
    adjust: 30,
    lumbar: 5
  };

  function numericValues(value) {
    const values = Array.isArray(value) ? value : [value];
    return values.map(item => parseInt(item, 10)).filter(item => !Number.isNaN(item));
  }

  // Calculate a weighted match percentage. Partial credit preserves the
  // previous quiz behaviour for a nearby budget/height/usage answer.
  function scoreProduct(product, userAnswers) {
    const quizData = product.metafields?.custom?.quiz_data;
    if (!quizData) return 0;

    let totalScore = 0;

    if (userAnswers.budget && quizData.budget) {
      const user = userAnswers.budget;
      const prod = parseInt(quizData.budget, 10);
      const diff = user - prod;
      if (diff === 0) totalScore += QUESTION_WEIGHTS.budget;
      else if (diff === 1) totalScore += QUESTION_WEIGHTS.budget * (2 / 3);
      else if (diff > 1) totalScore += QUESTION_WEIGHTS.budget * (1 / 3);
    }

    if (userAnswers.height && quizData.height) {
      const u = userAnswers.height;
      const arr = numericValues(quizData.height);
      if (arr.includes(u)) totalScore += QUESTION_WEIGHTS.height;
      else if (arr.includes(u - 1) || arr.includes(u + 1)) totalScore += QUESTION_WEIGHTS.height * (2 / 3);
    }

    const productHours = quizData.hours || quizData.usage;
    if (userAnswers.hours && productHours) {
      let u = userAnswers.hours;
      if (u === 4) u = 3;
      const arr = numericValues(productHours);
      if (arr.includes(u)) totalScore += QUESTION_WEIGHTS.hours;
      else if (arr.includes(u - 1) || arr.includes(u + 1)) totalScore += QUESTION_WEIGHTS.hours * (2 / 3);
    }

    if (userAnswers.adjust && quizData.adjust) {
      let u = userAnswers.adjust;
      if (u === 4) u = 3;
      const prod = parseInt(quizData.adjust, 10);
      if (prod >= u) totalScore += QUESTION_WEIGHTS.adjust;
      else if (prod === u - 1) totalScore += QUESTION_WEIGHTS.adjust * (2 / 3);
    }

    if (userAnswers.lumbar && quizData.lumbar) {
      const user = userAnswers.lumbar;
      const prod = parseInt(quizData.lumbar, 10);
      if (prod >= user) totalScore += QUESTION_WEIGHTS.lumbar;
      else if (prod === user - 1) totalScore += QUESTION_WEIGHTS.lumbar * (2 / 3);
    }

    return Math.max(0, Math.min(100, Math.round(totalScore)));
  }

  function removeDuplicateProducts(products) {
    const seen = new Set();
    const unique = [];

    products.forEach(p => {
      const id = (p.element && p.element.getAttribute) ? p.element.getAttribute("data-product-id") : null;
      if (!id) return;
      if (!seen.has(id)) {
        seen.add(id);
        unique.push(p);
      }
    });

    return unique;
  }

  // Find a reasonable wrapper for a product element (tries many common patterns)
  function findWrapperForElement(el) {
    if (!el) return null;

    const candidates = [
      '.winning-team__item',
      'slideshow-slide',
      '.swiper-slide',
      '.product-card',
      '.grid__item',
      '.card',
      '.collection__item',
      '.product-grid-item',
      '.product-card-wrapper',
      'li',
      '.product-item',
      '.grid-item'
    ];

    for (let sel of candidates) {
      try {
        const w = el.closest(sel);
        if (w) return w;
      } catch (e) {
        // ignore invalid selector errors
      }
    }

    // fallback: climb up until parent's children count > 1 and parent is not body/html
    let node = el;
    let steps = 0;
    while (node && node.parentElement && node.parentElement.tagName.toLowerCase() !== 'body' && steps < 6) {
      if (node.parentElement.children.length > 1) {
        return node.parentElement;
      }
      node = node.parentElement;
      steps++;
    }

    return el;
  }

  function removeElementSafely(el) {
    if (!el) return;
    try {
      if (el.remove) el.remove();
      else if (el.parentNode) el.parentNode.removeChild(el);
    } catch (e) {
      // ignore
    }
  }

  function getStoredValue(key) {
    try { return sessionStorage.getItem(key) || ''; }
    catch (e) { return ''; }
  }

  function productTrackingData(result, rank) {
    const card = result.element;
    const titleLink = card.querySelector('.winning-team-title a');
    const titleNode = titleLink || card.querySelector('.winning-team-title');
    const href = titleLink ? String(titleLink.getAttribute('href') || '') : '';
    const handleMatch = href.match(/\/products\/([^/?#]+)/);
    return {
      rank: rank,
      product_id: String(card.getAttribute('data-product-id') || ''),
      product_handle: handleMatch ? decodeURIComponent(handleMatch[1]) : '',
      product_title: titleNode ? String(titleNode.textContent || '').trim() : '',
      match_percent: result.score
    };
  }

  function sendQuizResults(topResults) {
    const params = new URLSearchParams(window.location.search);
    const quizId = params.get('quiz_id') || getStoredValue('quiz_id');
    const webhookUrl = getStoredValue('quiz_tracking_webhook');
    if (!quizId || !webhookUrl || webhookUrl.length <= 5) return;

    const sentKey = 'quiz_results_sent_' + quizId;
    try {
      if (sessionStorage.getItem(sentKey) === '1') return;
      sessionStorage.setItem(sentKey, '1');
    } catch (e) { /* Continue when storage is unavailable. */ }

    const payload = {
      event_type: 'quiz_results',
      quiz_id: quizId,
      quiz_source: params.get('quiz_source') || getStoredValue('quiz_source') ||
        (window.location.pathname.includes('quiz-teaser-result') ? 'Quiz Teaser' : 'Simple Quiz'),
      date: new Date().toLocaleString('en-IL', { timeZone: 'Asia/Jerusalem' }),
      name: getStoredValue('quiz_user_name'),
      phone: getStoredValue('quiz_phone'),
      email: params.get('email') || getStoredValue('quiz_email'),
      coupon_code: getStoredValue('quiz_coupon_code'),
      recommendations: topResults.map((result, index) => productTrackingData(result, index + 1))
    };

    try {
      const body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(webhookUrl, new Blob([body], { type: 'text/plain' }));
      } else {
        fetch(webhookUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'text/plain' },
          body: body,
          keepalive: true
        });
      }
    } catch (e) {
      console.warn('Quiz result tracking error:', e);
      try { sessionStorage.removeItem(sentKey); } catch (storageError) { /* ignore */ }
    }
  }

  function initQuizResults() {
    const userAnswers = getQuizAnswers();
    const resultRoot = document.querySelector('.winning-team-collection--quiz-result');
    if (!resultRoot) return;
    const track = resultRoot.querySelector('.winning-team__track');
    const productCards = Array.from(resultRoot.querySelectorAll('.winning-team-card[data-product-id][data-quiz-data]'));
    if (productCards.length === 0) return;

    const scoredProducts = [];

    // Score each product
    productCards.forEach(card => {
      const raw = card.getAttribute('data-quiz-data');
      if (!raw || raw === 'null') return;

      let quizData;
      try {
        quizData = JSON.parse(raw);
      } catch (e) {
        console.error("Invalid JSON:", raw);
        return;
      }

      const score = scoreProduct(
        { metafields: { custom: { quiz_data: quizData } } },
        userAnswers
      );

      scoredProducts.push({
        element: card,
        score,
        budget: parseInt(quizData.budget),
      });
    });

    if (scoredProducts.length === 0) return;

    // sort by highest score, tie-break by budget distance to user budget (closer wins),
    scoredProducts.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;

      const aDiff = Math.abs((a.budget || 0) - userAnswers.budget);
      const bDiff = Math.abs((b.budget || 0) - userAnswers.budget);
      if (aDiff !== bDiff) return aDiff - bDiff;

      return (a.budget || 0) - (b.budget || 0);
    });

    // remove duplicate DOM elements by product id (keeps first occurrence)
    const uniqueProducts = removeDuplicateProducts(scoredProducts);

    // ensure sorted order preserved after dedupe (stable)
    uniqueProducts.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const aDiff = Math.abs((a.budget || 0) - userAnswers.budget);
      const bDiff = Math.abs((b.budget || 0) - userAnswers.budget);
      if (aDiff !== bDiff) return aDiff - bDiff;
      return (a.budget || 0) - (b.budget || 0);
    });

    // Keep the ranking visible in development tools for metafield QA.
    try {
      console.groupCollapsed("Quiz product ranking");
      console.table(uniqueProducts.map(p => ({
        id: p.element.getAttribute('data-product-id'),
        match: p.score + '%',
        budget: p.budget
      })));
      console.groupEnd();
    } catch (e) { /* ignore */ }

    // Client-approved result format: exactly three chairs, with one highlighted.
    const topResults = uniqueProducts.slice(0, 3);
    const topIds = topResults.map(p => (p.element && p.element.getAttribute) ? p.element.getAttribute("data-product-id") : null);

    // Hide all cards first, scoped only to this result section.
    productCards.forEach(card => {
      const wrapper = findWrapperForElement(card) || card;
      try { wrapper.style.display = 'none'; } catch (e) { /* ignore */ }
    });

    // Remove every non-winning card from this section only.
    uniqueProducts.forEach(p => {
      const pid = (p.element && p.element.getAttribute) ? p.element.getAttribute("data-product-id") : null;
      if (!pid) return;
      if (!topIds.includes(pid)) {
        const wrapper = findWrapperForElement(p.element);
        removeElementSafely(wrapper);
      }
    });

    // Show, decorate and reorder the winners by their dynamic score.
    const selectedWrappers = [];
    topResults.forEach((result, index) => {
      const pid = result.element.getAttribute("data-product-id");
      const card = resultRoot.querySelector(`.winning-team-card[data-product-id="${pid}"]`) || result.element;
      const wrapper = findWrapperForElement(card) || card;
      wrapper.style.removeProperty('display');
      wrapper.style.display = 'block';
      wrapper.classList.remove('hidden', 'd-none', 'is-hidden');
      wrapper.removeAttribute('hidden');
      wrapper.classList.add(`quiz-result-rank-${index + 1}`);
      card.classList.toggle('quiz-best-match', index === 0);
      card.setAttribute('data-match-score', String(result.score));
      const percentage = card.querySelector('[data-quiz-match-percent]');
      if (percentage) percentage.textContent = result.score + '%';
      selectedWrappers.push(wrapper);
    });

    if (track) selectedWrappers.forEach(wrapper => track.appendChild(wrapper));

    // Expose selection globally for compare builders
    window.quizTopResults = {
      top: selectedWrappers,
      upgrade: null
    };
    sendQuizResults(topResults);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuizResults);
  } else {
    initQuizResults();
  }

})();


// Replace the buildQuizCompare function with this:
function buildQuizCompare(sectionEl, compareContainer){
    var selected = [];
    if (window.quizTopResults && window.quizTopResults.top) {
      selected = selected.concat(window.quizTopResults.top.slice(0,3));
    } else {
      selected = Array.from(sectionEl.querySelectorAll('.winning-team__item')).slice(0,3);
    }
    selected = selected.slice(0,3);

    // Build selectedProducts for compare page section API
    window.selectedProducts = [];
    selected.forEach(function(item){
      var card = item.querySelector('.winning-team-card') 
                || item.closest('.winning-team-card') 
                || item.querySelector('[data-product-id]');
      var pidGuess = '';
      if (card && card.dataset && card.dataset.productId) { pidGuess = card.dataset.productId; }
      else if (item && item.getAttribute) { pidGuess = item.getAttribute('data-product-id') || ''; }
      // If the found element is not the actual product card, resolve canonical element by class
      if (card && !card.classList.contains('winning-team-card')) {
        var pidLocal = card.getAttribute && card.getAttribute('data-product-id');
        if (!pidLocal) {
          var inner = item.querySelector('.winning-team-card[data-product-id]');
          if (inner) { card = inner; pidLocal = inner.getAttribute('data-product-id'); }
        }
        if (pidLocal) {
          var canon = document.querySelector('.winning-team-card[data-product-id="'+pidLocal+'"]');
          if (canon) card = canon;
        }
      }
      // Also if card lacks key datasets, try canonical lookup anyway
      if (card && !card.dataset) {
        var canon2 = document.querySelector('.winning-team-card[data-product-id="'+pidGuess+'"]');
        if (canon2) card = canon2;
      }
      if (!card && pidGuess) {
        var canonical = document.querySelector('.winning-team-card[data-product-id="'+pidGuess+'"]');
        if (!canonical) canonical = document.querySelector('[data-product-id="'+pidGuess+'"].winning-team-card');
        if (!canonical) canonical = document.querySelector('[data-product-id="'+pidGuess+'"][data-quiz-data]');
        if (canonical) card = canonical;
      }
      // Fallback: use compare thumbnails as canonical data source if available
      var metaEl = null;
      if (pidGuess) {
        metaEl = document.querySelector('.thumb-item[data-product-id="'+pidGuess+'"]');
        if (!metaEl) {
          // try any element exposing specs for this product id (e.g., color swatch buttons)
          var swatchEl = document.querySelector('button.color-variant-swatch[data-product-id="'+pidGuess+'"]');
          if (swatchEl) metaEl = swatchEl.closest('.thumb-item');
        }
      }
      if (!card && !metaEl) return;
      var titleEl = item.querySelector('.winning-team-title a') || (card ? card.querySelector('.winning-team-title a') : null);
      var title = titleEl?.textContent?.trim() || card?.dataset.productType || '';
      if (!title) {
        var tAlt = (card?.dataset?.productType || '').trim();
        if (tAlt) title = tAlt;
        else if (metaEl && metaEl.dataset && metaEl.dataset.title) {
          title = metaEl.dataset.title;
        }
      }
      var productType = title;
      var priceText = item.querySelector('.winning-team-price')?.textContent?.trim() || card?.dataset.price || '';
      if (!priceText && card && card.getAttribute('data-price')) priceText = card.getAttribute('data-price');
      if (!priceText && metaEl && metaEl.dataset && metaEl.dataset.price) priceText = metaEl.dataset.price;
      
      // Get currently active/selected color
      var colorEl = item.querySelector('.winning-team-swatch.is-active') || item.querySelector('.swatch.is-active') || (card ? card.querySelector('.swatch.is-active') : null);
      var selectedColor = colorEl?.getAttribute('aria-label') || item.querySelector('.winning-team-color')?.textContent?.trim() || card?.dataset.currentColor || '';
      if (!selectedColor && card) {
        var colorText = card.querySelector('.winning-team-color')?.textContent?.trim();
        if (colorText) selectedColor = colorText;
        else if (card.dataset && card.dataset.currentColor) selectedColor = card.dataset.currentColor;
      }
      if (!selectedColor && metaEl && metaEl.dataset && metaEl.dataset.currentColor) {
        selectedColor = metaEl.dataset.currentColor;
      }
      
      // Get active slide image/video for selected color
      var activeSlide = item.querySelector('.winning-team-slide.active') || item.querySelector('.winning-team-slide') || (card ? card.querySelector('.winning-team-slide.active') || card.querySelector('.winning-team-slide') : null);
      var activeSlideImg = activeSlide?.querySelector('img') || item.querySelector('.winning-team-image') || item.querySelector('img') || (card ? card.querySelector('img') : null);
      var activeSlideVideo = activeSlide?.querySelector('video') || (card ? card.querySelector('video') : null);
      
      var src = activeSlideImg ? (activeSlideImg.getAttribute('src') || activeSlideImg.getAttribute('data-src') || '') : '';
      var activeVideoSources = '';
      if (!src && metaEl && metaEl.dataset && metaEl.dataset.image) src = metaEl.dataset.image;
      
      if (activeSlideVideo) {
        var sources = activeSlideVideo.querySelectorAll('source');
        var videoSourcesArr = [];
        sources.forEach(function(source) {
          var url = source.getAttribute('src');
          var type = source.getAttribute('type');
          if (url && type) {
            videoSourcesArr.push(url + '|' + type);
          }
        });
        activeVideoSources = videoSourcesArr.join(';');
      }
      if (!activeVideoSources && metaEl && metaEl.dataset && metaEl.dataset.videoSources) {
        activeVideoSources = metaEl.dataset.videoSources;
      }
      
      var linkEl = item.querySelector('.winning-team-link') || (card ? card.querySelector('.winning-team-link') : null);
      var handle = linkEl?.getAttribute('href') || card?.dataset?.handle || metaEl?.dataset?.handle || '';
      if (handle.startsWith('/products/')) handle = handle.replace('/products/', '');
      
      // Collect ALL variants (all color options with their media)
      var variants = [];
      var allSwatches = item.querySelectorAll('.winning-team-swatch, .swatch');
      if (allSwatches.length === 0 && card) allSwatches = card.querySelectorAll('.winning-team-swatch, .swatch');
      if (allSwatches.length === 0 && metaEl) allSwatches = metaEl.querySelectorAll('button.color-variant-swatch.swatch');
      var allSlides = item.querySelectorAll('.winning-team-slide');
      if (allSlides.length === 0 && card) allSlides = card.querySelectorAll('.winning-team-slide');
      if (allSlides.length === 0 && metaEl) allSlides = metaEl.querySelectorAll('.winning-team-slide');
      
      allSwatches.forEach(function(swatch, index) {
        var variantColor = swatch.getAttribute('aria-label') || swatch.getAttribute('data-color') || '';
        if (!variantColor) return;
        
        // Find corresponding slide for this color variant
        var slide = allSlides[index];
        if (!slide && swatch.dataset && (swatch.dataset.image || swatch.dataset.videoSources)) {
          variants.push({
            color: variantColor,
            image: swatch.dataset.image || '',
            videoSources: swatch.dataset.videoSources || ''
          });
          return;
        }
        if (!slide) return;
        
        var variantImg = slide.querySelector('img');
        var variantVideo = slide.querySelector('video');
        var variantImgSrc = variantImg ? (variantImg.getAttribute('src') || variantImg.getAttribute('data-src') || '') : '';
        var variantVideoSources = '';
        
        if (variantVideo) {
          var vSources = variantVideo.querySelectorAll('source');
          var vSourcesArr = [];
          vSources.forEach(function(source) {
            var url = source.getAttribute('src');
            var type = source.getAttribute('type');
            if (url && type) {
              vSourcesArr.push(url + '|' + type);
            }
          });
          variantVideoSources = vSourcesArr.join(';');
        }
        
        variants.push({
          color: variantColor,
          image: variantImgSrc,
          videoSources: variantVideoSources
        });
      });

      window.selectedProducts.push({
        id: card?.dataset.productId || '',
        title: title || 'ללא',
        productType: productType || 'ללא',
        image: src || '',
        selectedColor: selectedColor || '',
        videoSources: activeVideoSources || card?.dataset.videoSources || '',
        variants: variants, // All color variants with their media
        tilting_support: card?.dataset.tiltingSupport || metaEl?.dataset?.tiltingSupport || '',
        certificate: card?.dataset.certificate || metaEl?.dataset?.certificate || '',
        mesh_or_cushions: card?.dataset.meshOrCushions || metaEl?.dataset?.meshOrCushions || '',
        lumbar_support: card?.dataset.lumbar || metaEl?.dataset?.lumbar || '',
        adjustability: card?.dataset.adjustability || metaEl?.dataset?.adjustability || '',
        usage: card?.dataset.usage || metaEl?.dataset?.usage || '',
        fit_to_height: card?.dataset.fitToHeight || metaEl?.dataset?.fitToHeight || '',
        weight_of_the_chair: card?.dataset.weight || card?.dataset.weightOfTheChair || metaEl?.dataset?.weight || '',
        awards: card?.dataset.awards || metaEl?.dataset?.awards || '',
        foldable_chair: card?.dataset.foldable || metaEl?.dataset?.foldable || '',
        castors_wheels: card?.dataset.castors || metaEl?.dataset?.castors || '',
        sliding_sit: card?.dataset.slidingSit || metaEl?.dataset?.slidingSit || '',
        headrest: card?.dataset.headrest || metaEl?.dataset?.headrest || '',
        handrest: card?.dataset.handrest || metaEl?.dataset?.handrest || '',
        base: card?.dataset.base || metaEl?.dataset?.base || '',
        features: card?.dataset.features || metaEl?.dataset?.features || '',
        newton_executive: card?.dataset.newtonExecutive || metaEl?.dataset?.newtonExecutive || '',
        newton_quickfold: card?.dataset.newtonQuickfold || metaEl?.dataset?.newtonQuickfold || '',
        colors: card?.dataset.colors || metaEl?.dataset?.colors || '',
        price: card?.dataset.price || priceText || metaEl?.dataset?.price || '',
        handle: handle || metaEl?.dataset?.handle || ''
      });
    });

    console.log('🟢 Quiz Compare - selectedProducts:', JSON.stringify(window.selectedProducts));

    // Rest of the function remains same...

    // ✅ FIX: Wait for DOM to be ready before calling compare function
    if (typeof window.setCompareSelectedProducts === 'function') {
      // Add small delay to ensure DOM is fully rendered
      setTimeout(function() {
        window.setCompareSelectedProducts(window.selectedProducts);
      }, 100);
    } else {
      var attempts = 0;
      var timer = setInterval(function(){
        attempts++;
        if (typeof window.setCompareSelectedProducts === 'function') {
          clearInterval(timer);
          // Add small delay here too
          setTimeout(function() {
            window.setCompareSelectedProducts(window.selectedProducts);
          }, 100);
        } else if (attempts > 40) {
          clearInterval(timer);
          // Fallback: manually click compare button
          var compareBtn = document.getElementById('compare-btn');
          if (compareBtn) { 
            setTimeout(function() {
              compareBtn.click(); 
            }, 100);
          }
        }
      }, 100);
    }
}
